"use client";

import { setAnalyticsConsentSnapshot } from "@analytics/lib/analytics-consent-gate";
import { readSqSidCookie } from "@analytics/lib/identity-cookie";
import { resetIdentityClientState } from "@analytics/lib/identity-resolution";
import { optOutAnalytics } from "@analytics/lib/opt-out-analytics";
import { ActiveAnalyticsProvider } from "@analytics/lib/resolve-provider";
import { clearVisitorBootstrapSnapshot } from "@analytics/lib/visitor-bootstrap-cache";
import {
	markConsentSyncPending,
	rememberConsentPublishFlow,
	syncSiteCmpConsentToServer,
} from "@shared/lib/consent/consent-server-sync";
import {
	clearSiteCmpConsentClientState,
	registerConsentRecordListener,
} from "@shared/lib/consent/consent-sync-bridge";
import {
	CONSENT_VERSION,
	type ConsentCategory,
	type ConsentRecord,
	type ConsentStatus,
	DEFAULT_CONSENT_CATS,
} from "@shared/lib/consent/constants";
import { clearPreferenceStorage } from "@shared/lib/consent/preferences-consent-storage";
import { buildDecidedConsentRecord } from "@shared/lib/consent/record";
import {
	detectPrivacyRejectionSignal,
	persistConsentRecord,
	persistPrivacyRejectedConsent,
	reconcileConsentFromStorage,
	removeLegacyConsentCookie,
	writeConsentLocalStorage,
} from "@shared/lib/consent/storage-client";
import { orpcClient } from "@shared/lib/orpc-client";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

export interface ConsentContextValue {
	status: ConsentStatus;
	record: ConsentRecord | null;
	hasConsent: (cat: ConsentCategory) => boolean;
	acceptAll: () => void;
	rejectNonEssential: () => void;
	savePartial: (partial: Partial<Record<ConsentCategory, boolean>>) => void;
	/**
	 * Opens the preferences dialog. Optional `draftSeed` merges into the draft
	 * (not the baseline) so e.g. Preferences can be pre-toggled until Save.
	 */
	open: (draftSeed?: Partial<Record<ConsentCategory, boolean>>) => void;
	close: () => void;
	/** True while the user has not saved a decision (first visit or post version bump). */
	bannerOpen: boolean;
	dialogOpen: boolean;
	/** Seed applied when the dialog opens; cleared on close. */
	dialogDraftSeed: Partial<Record<ConsentCategory, boolean>> | null;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

function clearDeclinedAnalyticsClient(): void {
	optOutAnalytics();
	resetIdentityClientState();
	clearVisitorBootstrapSnapshot();
}

export function ConsentProvider({
	children,
	initialRecord,
}: {
	children: ReactNode;
	initialRecord?: ConsentRecord | null;
}) {
	const [record, setRecord] = useState<ConsentRecord | null>(
		() => initialRecord ?? null,
	);
	const [status, setStatus] = useState<ConsentStatus>(() =>
		initialRecord != null ? "decided" : "unknown",
	);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogDraftSeed, setDialogDraftSeed] = useState<Partial<
		Record<ConsentCategory, boolean>
	> | null>(null);
	const hydratedRef = useRef(false);
	const prevAnalyticsConsentRef = useRef<boolean | null>(
		initialRecord != null ? Boolean(initialRecord.cats.analytics) : null,
	);

	const prevPreferencesConsentRef = useRef<boolean | null>(
		initialRecord != null ? Boolean(initialRecord.cats.preferences) : null,
	);

	const bannerOpen = status === "unknown";

	const publishConsentRecord = useCallback(
		(next: ConsentRecord, flow: string) => {
			persistConsentRecord(next);
			setRecord(next);
			setStatus("decided");
			setAnalyticsConsentSnapshot("decided", {
				analyticsGranted: Boolean(next.cats.analytics),
				preferencesGranted: Boolean(next.cats.preferences),
			});
			if (!next.cats.preferences) {
				clearPreferenceStorage();
			}
			rememberConsentPublishFlow(flow);
			ActiveAnalyticsProvider.recordConsentChoice?.({
				flow,
				consent_categories: next.cats,
			});
			const sqid = readSqSidCookie();
			if (sqid) {
				void syncSiteCmpConsentToServer(sqid, flow).then((synced) => {
					if (!synced) {
						markConsentSyncPending();
					}
				});
			} else {
				markConsentSyncPending();
			}
		},
		[],
	);

	useLayoutEffect(() => {
		setAnalyticsConsentSnapshot(
			status === "decided" ? "decided" : "unknown",
			{
				analyticsGranted:
					status === "decided" && record != null
						? Boolean(record.cats.analytics)
						: false,
				preferencesGranted:
					status === "decided" && record != null
						? Boolean(record.cats.preferences)
						: false,
			},
		);
	}, [record, status]);

	useEffect(() => {
		const next = Boolean(record?.cats.analytics);
		const prev = prevAnalyticsConsentRef.current;
		prevAnalyticsConsentRef.current = next;
		if (record == null) {
			return;
		}
		if (prev === true && next === false) {
			if (ActiveAnalyticsProvider.notifyConsentRevokedBeforeOptOut) {
				ActiveAnalyticsProvider.notifyConsentRevokedBeforeOptOut({
					consent_categories: record.cats,
				});
			} else {
				clearDeclinedAnalyticsClient();
			}
			resetIdentityClientState();
			clearVisitorBootstrapSnapshot();
			return;
		}
		if (!next) {
			clearDeclinedAnalyticsClient();
		}
	}, [record]);

	useEffect(() => {
		const next = Boolean(record?.cats.preferences);
		const prev = prevPreferencesConsentRef.current;
		prevPreferencesConsentRef.current = next;
		if (prev === true && next === false) {
			clearPreferenceStorage();
		}
	}, [record]);

	useEffect(() => {
		registerConsentRecordListener((nextRecord) => {
			if (nextRecord == null) {
				setAnalyticsConsentSnapshot("unknown", {
					analyticsGranted: false,
					preferencesGranted: false,
				});
				setRecord(null);
				setStatus("unknown");
				clearDeclinedAnalyticsClient();
				return;
			}
			setAnalyticsConsentSnapshot("decided", {
				analyticsGranted: Boolean(nextRecord.cats.analytics),
				preferencesGranted: Boolean(nextRecord.cats.preferences),
			});
			setRecord(nextRecord);
			setStatus("decided");
		});

		void orpcClient.identity
			.getSiteCmpPolicy()
			.then((policy) => {
				if (!policy.isRetired) {
					return;
				}
				clearSiteCmpConsentClientState();
				setRecord(null);
				setStatus("unknown");
				clearDeclinedAnalyticsClient();
			})
			.catch(() => {
				// Policy metadata is best-effort on first paint.
			});

		return () => {
			registerConsentRecordListener(null);
		};
	}, []);

	useEffect(() => {
		if (hydratedRef.current) {
			return;
		}
		hydratedRef.current = true;
		removeLegacyConsentCookie();

		if (initialRecord != null) {
			writeConsentLocalStorage(initialRecord);
			setAnalyticsConsentSnapshot("decided", {
				analyticsGranted: Boolean(initialRecord.cats.analytics),
				preferencesGranted: Boolean(initialRecord.cats.preferences),
			});
			if (!initialRecord.cats.preferences) {
				clearPreferenceStorage();
			}
			if (!initialRecord.cats.analytics) {
				clearDeclinedAnalyticsClient();
			}
			return;
		}

		const reconciled = reconcileConsentFromStorage();
		if (reconciled != null) {
			setRecord(reconciled);
			setStatus("decided");
			setAnalyticsConsentSnapshot("decided", {
				analyticsGranted: Boolean(reconciled.cats.analytics),
				preferencesGranted: Boolean(reconciled.cats.preferences),
			});
			persistConsentRecord(reconciled);
			if (!reconciled.cats.preferences) {
				clearPreferenceStorage();
			}
			if (!reconciled.cats.analytics) {
				clearDeclinedAnalyticsClient();
			}
			return;
		}

		if (detectPrivacyRejectionSignal()) {
			const rejected = persistPrivacyRejectedConsent();
			setRecord(rejected);
			setStatus("decided");
			setAnalyticsConsentSnapshot("decided", {
				analyticsGranted: Boolean(rejected.cats.analytics),
				preferencesGranted: Boolean(rejected.cats.preferences),
			});
			ActiveAnalyticsProvider.recordConsentChoice?.({
				flow: "privacy_signal_reject",
				consent_categories: rejected.cats,
			});
			if (!rejected.cats.preferences) {
				clearPreferenceStorage();
			}
			clearDeclinedAnalyticsClient();
		}
	}, [initialRecord]);

	const hasConsent = useCallback(
		(cat: ConsentCategory) => {
			if (cat === "necessary") {
				return true;
			}
			if (status !== "decided" || record == null) {
				return false;
			}
			return Boolean(record.cats[cat]);
		},
		[status, record],
	);

	const acceptAll = useCallback(() => {
		const next = buildDecidedConsentRecord({
			preferences: true,
			analytics: true,
			marketing: true,
		});
		publishConsentRecord(next, "accept_all");
		setDialogDraftSeed(null);
		setDialogOpen(false);
	}, [publishConsentRecord]);

	const rejectNonEssential = useCallback(() => {
		const next = buildDecidedConsentRecord({
			preferences: false,
			analytics: false,
			marketing: false,
		});
		publishConsentRecord(next, "reject_non_essential");
		setDialogDraftSeed(null);
		setDialogOpen(false);
	}, [publishConsentRecord]);

	const savePartial = useCallback(
		(partial: Partial<Record<ConsentCategory, boolean>>) => {
			const cats: Record<ConsentCategory, boolean> = {
				...DEFAULT_CONSENT_CATS,
				...(record?.cats ?? {}),
				...partial,
				necessary: true,
			};
			const next: ConsentRecord = {
				v: CONSENT_VERSION,
				ts: Date.now(),
				cats,
			};
			publishConsentRecord(next, "customize_preferences");
			setDialogDraftSeed(null);
			setDialogOpen(false);
		},
		[publishConsentRecord, record],
	);

	const open = useCallback(
		(draftSeed?: Partial<Record<ConsentCategory, boolean>>) => {
			setDialogDraftSeed(draftSeed ?? null);
			setDialogOpen(true);
		},
		[],
	);

	const close = useCallback(() => {
		setDialogOpen(false);
		setDialogDraftSeed(null);
	}, []);

	const value = useMemo<ConsentContextValue>(
		() => ({
			status,
			record,
			hasConsent,
			acceptAll,
			rejectNonEssential,
			savePartial,
			open,
			close,
			bannerOpen,
			dialogOpen,
			dialogDraftSeed,
		}),
		[
			status,
			record,
			hasConsent,
			acceptAll,
			rejectNonEssential,
			savePartial,
			open,
			close,
			bannerOpen,
			dialogOpen,
			dialogDraftSeed,
		],
	);

	return (
		<ConsentContext.Provider value={value}>
			{children}
		</ConsentContext.Provider>
	);
}

export function useConsent(): ConsentContextValue {
	const ctx = useContext(ConsentContext);
	if (ctx == null) {
		throw new Error("useConsent must be used within ConsentProvider");
	}
	return ctx;
}
